import { Box, Card, CardContent, Typography, useTheme, alpha } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { motion } from 'framer-motion';

/**
 * StatCard - карточка статистики в стиле MD3
 * 
 * @param {string} title - заголовок
 * @param {string|number} value - основное значение
 * @param {string} subtitle - подпись под значением
 * @param {ReactNode} icon - иконка
 * @param {string} color - цвет: primary, secondary, success, warning, error, info
 * @param {string} trend - направление тренда: 'up' | 'down'
 * @param {string} trendValue - значение тренда (например: "+12%")
 * @param {function} onClick - обработчик клика
 */
export default function StatCard({
  title,
  value,
  subtitle,
  icon,
  color = 'primary',
  trend,
  trendValue,
  onClick,
}) {
  const theme = useTheme();
  
  // Получаем цвета для карточки
  const colorMap = {
    primary: {
      bg: theme.palette.primary.light, // primaryContainer
      text: theme.palette.primary.dark, // onPrimaryContainer
      icon: theme.palette.primary.main,
    },
    secondary: {
      bg: theme.palette.secondary.light,
      text: theme.palette.secondary.dark,
      icon: theme.palette.secondary.main,
    },
    success: {
      bg: theme.palette.success.light,
      text: theme.palette.success.dark,
      icon: theme.palette.success.main,
    },
    warning: {
      bg: theme.palette.warning.light,
      text: theme.palette.warning.dark,
      icon: theme.palette.warning.main,
    },
    error: {
      bg: theme.palette.error.light,
      text: theme.palette.error.dark,
      icon: theme.palette.error.main,
    },
    info: {
      bg: theme.palette.info.light,
      text: theme.palette.info.dark,
      icon: theme.palette.info.main,
    },
  };
  
  const colors = colorMap[color] || colorMap.primary;
  
  return (
    <Card
      component={motion.div}
      whileHover={onClick ? { scale: 1.02 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        // Tonal surface
        bgcolor: alpha(colors.bg, 0.3),
        '&:hover': onClick ? {
          bgcolor: alpha(colors.bg, 0.4),
        } : undefined,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header row: icon + title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Icon container */}
          {icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: colors.bg,
                color: colors.text,
                '& .MuiSvgIcon-root': {
                  fontSize: 24,
                },
              }}
            >
              {icon}
            </Box>
          )}
          
          {/* Trend indicator */}
          {trend && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                bgcolor: trend === 'up' 
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.error.main, 0.1),
              }}
            >
              {trend === 'up' ? (
                <TrendingUp 
                  sx={{ 
                    fontSize: 16, 
                    color: 'success.main',
                  }} 
                />
              ) : (
                <TrendingDown 
                  sx={{ 
                    fontSize: 16, 
                    color: 'error.main',
                  }} 
                />
              )}
              <Typography
                variant="labelSmall"
                sx={{
                  color: trend === 'up' ? 'success.main' : 'error.main',
                  fontWeight: 600,
                }}
              >
                {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Value */}
        <Typography
          variant="headlineLarge"
          component="div"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          {value}
        </Typography>
        
        {/* Title */}
        <Typography
          variant="titleMedium"
          color="text.secondary"
          sx={{ mb: subtitle ? 0.5 : 0 }}
        >
          {title}
        </Typography>
        
        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{ opacity: 0.7 }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
